import { z } from "zod";
import { chatAgentIdSchema } from "./agent-ids.ts";
import {
  DEFAULT_CHAT_THINKING_EFFORT,
  selectedChatModelValues,
  selectedChatThinkingEffortValues,
} from "./models.ts";
import { chatSeedTagsSchema } from "../insight-context.ts";
import { defaultDashboardDescriptorSchema } from "../default-dashboards.ts";

/** Base tracify context (date range, presets, filters, etc.) */
export const tracifyContextSchema = z.object({
  selectedPresetId: z.string(),
  selectedStoreIds: z.array(z.string()),
  selectedStartDate: z.string(),
  selectedEndDate: z.string(),
  compared: z.boolean().optional(),
  comparisonPreset: z.string().nullish(),
  comparisonStartDate: z.string().nullish(),
  comparisonEndDate: z.string().nullish(),
  newVsReturning: z.enum(["new", "returning", "total"]).nullish(),
  granularity: z.enum(["none", "daily", "weekly", "monthly"]).nullish(),
  filters: z.string().nullish(),
  channels: z.array(z.string()).nullish(),
  topN: z.number().nullish(),
  rankBy: z.string().nullish(),
});

export type TracifyContext = z.infer<typeof tracifyContextSchema>;

const CONTEXT_TAG = "context";
const CONTEXT_BLOCK_REGEX = new RegExp(
  `<${CONTEXT_TAG}>[\\s\\S]*?<\\/${CONTEXT_TAG}>`,
  "gi"
);
const CONTEXT_BLOCK_EXACT_REGEX = new RegExp(
  `^\\s*<${CONTEXT_TAG}>([\\s\\S]*?)<\\/${CONTEXT_TAG}>\\s*$`
);

export function buildTracifyContextHash(context: TracifyContext): string {
  const stableStoreIds = [...context.selectedStoreIds].sort();
  const stableChannels = context.channels ? [...context.channels].sort() : null;

  return [
    context.selectedPresetId,
    stableStoreIds.join(","),
    context.selectedStartDate,
    context.selectedEndDate,
    context.compared ? "compare=1" : "compare=0",
    context.comparisonPreset ?? "",
    context.comparisonStartDate ?? "",
    context.comparisonEndDate ?? "",
    context.newVsReturning ?? "",
    context.granularity ?? "",
    context.filters ?? "",
    stableChannels?.join(",") ?? "",
    context.topN?.toString() ?? "",
    context.rankBy ?? "",
  ].join("|");
}

export const chatRuntimeContextSchema = z
  .object({
    preset_id: z.string().optional(),
    store_ids: z.array(z.string()).optional(),
    start_date: z.string().optional(),
    end_date: z.string().optional(),
    comparison_preset: z.string().nullish(),
    comparison_start_date: z.string().nullish(),
    comparison_end_date: z.string().nullish(),
    customer_type: z.enum(["new", "returning", "total"]).nullish(),
    granularity: tracifyContextSchema.shape.granularity,
    filters: tracifyContextSchema.shape.filters,
    channels: tracifyContextSchema.shape.channels,
    top_n: z.number().nullish(),
    rank_by: z.string().nullish(),
    scope_key: z.string().optional(),
    tagged: chatSeedTagsSchema.shape.tagged,
    default_dashboard: defaultDashboardDescriptorSchema.optional(),
  })
  .strict();

export type ChatRuntimeContext = z.infer<typeof chatRuntimeContextSchema>;

function sortObjectKeysDeep(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => sortObjectKeysDeep(item));
  }

  if (!value || typeof value !== "object") {
    return value;
  }

  const sortedEntries = Object.entries(value as Record<string, unknown>)
    .filter(([, entryValue]) => entryValue !== undefined)
    .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
    .map(([key, entryValue]) => [key, sortObjectKeysDeep(entryValue)] as const);

  return Object.fromEntries(sortedEntries);
}

function normalizeRuntimeContext(
  context: ChatRuntimeContext
): ChatRuntimeContext {
  return {
    ...context,
    store_ids: context.store_ids
      ? [...context.store_ids].sort()
      : context.store_ids,
    channels: context.channels
      ? [...context.channels].sort()
      : context.channels,
    tagged: context.tagged
      ? [...context.tagged].sort((left, right) =>
          `${left.type}:${left.id}`.localeCompare(`${right.type}:${right.id}`)
        )
      : context.tagged,
  };
}

export function serializeRuntimeContextText(
  context: ChatRuntimeContext
): string {
  const parsedContext = chatRuntimeContextSchema.parse(context);
  const normalizedContext = normalizeRuntimeContext(parsedContext);
  const stableContext = sortObjectKeysDeep(normalizedContext);
  return `<${CONTEXT_TAG}>${JSON.stringify(stableContext)}</${CONTEXT_TAG}>`;
}

export function isRuntimeContextText(text: string): boolean {
  return CONTEXT_BLOCK_EXACT_REGEX.test(text);
}

export function parseRuntimeContextText(
  text: string
): ChatRuntimeContext | null {
  const match = text.match(CONTEXT_BLOCK_EXACT_REGEX);
  if (!match) {
    return null;
  }

  const rawPayload = match[1]?.trim();
  if (!rawPayload) {
    return null;
  }

  try {
    const parsedPayload = JSON.parse(rawPayload);
    const parsedContext = chatRuntimeContextSchema.safeParse(parsedPayload);
    return parsedContext.success ? parsedContext.data : null;
  } catch {
    return null;
  }
}

export const runtimeContextDataPartSchema = z.object({
  type: z.literal("data-runtimeContext"),
  data: chatRuntimeContextSchema,
});

export function stripRuntimeContextText(text: string): string {
  return text.replace(CONTEXT_BLOCK_REGEX, "").trim();
}

export const tracifyToolAuthRequestContextSchema = z.object({
  token: z.string(),
});

export const tracifyToolOrganisationRequestContextSchema =
  tracifyToolAuthRequestContextSchema.extend({
    organisationId: z.string().min(1),
  });

const selectedStoreIdsRequestContextSchema = z.array(z.string().min(1)).min(1);

/**
 * Analytics read tools always run against the selected store scope from the
 * current request context, not agent-provided store ids.
 */
export const tracifyToolAnalyticsRequestContextSchema =
  tracifyToolAuthRequestContextSchema.extend({
    selectedStoreIds: selectedStoreIdsRequestContextSchema,
  });

export const postChatRequestContextSchema = z
  .object({
    selectedChatModel: z.enum(selectedChatModelValues),
    selectedThinkingEffort: z
      .enum(selectedChatThinkingEffortValues)
      .default(DEFAULT_CHAT_THINKING_EFFORT),
    sessionId: z.string().optional(),
    organisationId: z.string().nullish(),
    selectedStoreIds: selectedStoreIdsRequestContextSchema.optional(),
  })
  .strict();

export type PostChatRequestContext = z.infer<
  typeof postChatRequestContextSchema
>;

/** Full request context with auth and session identifiers */
export const tracifyRequestContextValuesSchema =
  postChatRequestContextSchema.extend({
    userId: z.string(),
    chatId: z.string(),
    token: z.string(),
    selectedStoreIds: selectedStoreIdsRequestContextSchema.optional(),
  });

export type TracifyRequestContextValues = z.infer<
  typeof tracifyRequestContextValuesSchema
>;

const HTTP_URL_PATTERN = /^https?:\/\//i;
const DATA_URL_PATTERN = /^data:/i;
const IMAGE_MEDIA_TYPE_PATTERN = /^image\/[a-z0-9.+-]+$/i;

const textPartSchema = z.object({
  type: z.literal("text"),
  text: z.string().min(1).max(2000),
});

const persistedTextPartSchema = z
  .object({
    type: z.literal("text"),
    text: z.string(),
    state: z.enum(["streaming", "done"]).optional(),
  })
  .passthrough();

const contextTextPartSchema = z.object({
  type: z.literal("text"),
  text: z.string().min(1).max(12000).refine(isRuntimeContextText, {
    message: "Invalid context text wrapper",
  }),
});

const filePartSchema = z.object({
  type: z.literal("file"),
  mediaType: z.enum(["image/jpeg", "image/png"]),
  name: z.string().min(1).max(100),
  url: z.string().refine((value) => HTTP_URL_PATTERN.test(value), {
    message: "Invalid URL",
  }),
});

function isSupportedMultimodalToolUrl(value: string) {
  return HTTP_URL_PATTERN.test(value) || DATA_URL_PATTERN.test(value);
}

export const multimodalToolTextPartSchema = z
  .object({
    type: z.literal("text"),
    text: z.string().min(1).max(8000),
  })
  .strict();

export const multimodalToolFilePartSchema = z
  .object({
    type: z.literal("file"),
    mediaType: z.string().regex(IMAGE_MEDIA_TYPE_PATTERN, {
      message: "Invalid image media type",
    }),
    filename: z.string().min(1).max(255).optional(),
    url: z.string().refine(isSupportedMultimodalToolUrl, {
      message: "Invalid URL",
    }),
  })
  .strict();

export const multimodalToolUserMessagePartSchema = z.union([
  multimodalToolTextPartSchema,
  multimodalToolFilePartSchema,
]);

export const multimodalToolUserMessageSchema = z
  .object({
    id: z.string().min(1).optional(),
    role: z.literal("user"),
    parts: z
      .array(multimodalToolUserMessagePartSchema)
      .min(1)
      .superRefine((parts, context) => {
        const hasTextPart = parts.some((part) => part.type === "text");

        if (!hasTextPart) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            message: "At least one text part is required.",
          });
        }
      }),
  })
  .strict();

export type MultimodalToolTextPart = z.infer<
  typeof multimodalToolTextPartSchema
>;
export type MultimodalToolFilePart = z.infer<
  typeof multimodalToolFilePartSchema
>;
export type MultimodalToolUserMessagePart = z.infer<
  typeof multimodalToolUserMessagePartSchema
>;
export type MultimodalToolUserMessage = z.infer<
  typeof multimodalToolUserMessageSchema
>;

const partSchema = z.union([
  textPartSchema,
  contextTextPartSchema,
  filePartSchema,
  runtimeContextDataPartSchema,
]);

const persistedFilePartSchema = z
  .object({
    type: z.literal("file"),
    mediaType: z.string().min(1),
    filename: z.string().min(1).max(255).optional(),
    name: z.string().min(1).max(255).optional(),
    url: z.string().refine(isSupportedMultimodalToolUrl, {
      message: "Invalid URL",
    }),
  })
  .passthrough();

const reasoningPartSchema = z
  .object({
    type: z.literal("reasoning"),
    text: z.string(),
    state: z.enum(["streaming", "done"]).optional(),
  })
  .passthrough();

const stepStartPartSchema = z
  .object({
    type: z.literal("step-start"),
  })
  .passthrough();

const unknownDataPartSchema = z
  .object({
    type: z.string().regex(/^data-[A-Za-z0-9_-]+$/),
    id: z.string().min(1).optional(),
    data: z.unknown(),
  })
  .passthrough()
  .refine((part) => part.type !== "data-runtimeContext", {
    message: "Invalid runtimeContext data part",
  });

const toolApprovalPartSchema = z
  .object({
    id: z.string().min(1),
    approved: z.boolean().optional(),
    reason: z.string().optional(),
    isAutomatic: z.boolean().optional(),
  })
  .passthrough();

const toolPartSchema = z
  .object({
    type: z.string().regex(/^tool-[A-Za-z0-9_-]+$/),
    toolCallId: z.string().min(1),
    state: z.enum([
      "input-streaming",
      "input-available",
      "approval-requested",
      "approval-responded",
      "output-available",
      "output-error",
      "output-denied",
    ]),
    input: z.unknown().optional(),
    rawInput: z.unknown().optional(),
    output: z.unknown().optional(),
    errorText: z.string().optional(),
    approval: toolApprovalPartSchema.optional(),
    providerExecuted: z.boolean().optional(),
    preliminary: z.boolean().optional(),
  })
  .passthrough();

const persistedUserPartSchema = z.union([
  persistedTextPartSchema,
  persistedFilePartSchema,
  runtimeContextDataPartSchema,
  unknownDataPartSchema,
]);

const assistantPartSchema = z.union([
  persistedTextPartSchema,
  reasoningPartSchema,
  persistedFilePartSchema,
  runtimeContextDataPartSchema,
  unknownDataPartSchema,
  toolPartSchema,
  stepStartPartSchema,
]);

const userMessageSchema = z.object({
  id: z.string().min(1),
  role: z.literal("user"),
  parts: z.array(partSchema),
});

const snapshotUserMessageSchema = z
  .object({
    id: z.string().min(1),
    role: z.literal("user"),
    metadata: z.unknown().optional(),
    parts: z.array(persistedUserPartSchema),
  })
  .passthrough();

const assistantMessageSchema = z
  .object({
    id: z.string().min(1),
    role: z.literal("assistant"),
    metadata: z.unknown().optional(),
    parts: z.array(assistantPartSchema),
  })
  .passthrough();

const chatSnapshotMessageSchema = z.union([
  snapshotUserMessageSchema,
  assistantMessageSchema,
]);

const postChatRequestBaseSchema = z
  .object({
    agentId: chatAgentIdSchema,
    id: z.string(),
    trigger: z.enum(["submit-message", "regenerate-message"]),
    messageId: z.string().optional(),
    requestContext: postChatRequestContextSchema,
  })
  .strict();

export const postChatRequestWithMessageBodySchema = postChatRequestBaseSchema
  .extend({
    message: userMessageSchema,
  })
  .strict()
  .superRefine((value, context) => {
    if (value.trigger !== "submit-message") {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Single-message requests are only supported for submit-message.",
        path: ["trigger"],
      });
    }
  });

export const postChatRequestWithMessagesBodySchema = postChatRequestBaseSchema
  .extend({
    messages: z.array(chatSnapshotMessageSchema).min(1),
  })
  .strict()
  .superRefine((value, context) => {
    if (
      value.trigger === "regenerate-message" &&
      (typeof value.messageId !== "string" || value.messageId.length === 0)
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "messageId is required for regenerate-message requests.",
        path: ["messageId"],
      });
    }

    if (
      value.trigger === "submit-message" &&
      !value.messages.some((message) => {
        const parsedMessage = snapshotUserMessageSchema.safeParse(message);
        return parsedMessage.success;
      })
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Submit requests require at least one user message.",
        path: ["messages"],
      });
    }
  });

export const postChatRequestBodySchema = z.union([
  postChatRequestWithMessageBodySchema,
  postChatRequestWithMessagesBodySchema,
]);

export type PostChatRequestBody = z.infer<typeof postChatRequestBodySchema>;

type MessagePartLike = { type?: unknown; data?: unknown; text?: unknown };
type MessageLike = { parts?: unknown };

function parseRuntimeContextPart(
  part: MessagePartLike
): ChatRuntimeContext | null {
  if (part.type === "data-runtimeContext") {
    const parsed = runtimeContextDataPartSchema.safeParse(part);
    return parsed.success ? parsed.data.data : null;
  }

  if (part.type === "text" && typeof part.text === "string") {
    return parseRuntimeContextText(part.text);
  }

  return null;
}

export function extractLatestRuntimeContextFromMessages(
  messages: MessageLike[]
): ChatRuntimeContext | null {
  for (
    let messageIndex = messages.length - 1;
    messageIndex >= 0;
    messageIndex -= 1
  ) {
    const parts = messages[messageIndex]?.parts;
    if (!Array.isArray(parts)) {
      continue;
    }

    for (let partIndex = parts.length - 1; partIndex >= 0; partIndex -= 1) {
      const part = parts[partIndex] as MessagePartLike;
      const context = parseRuntimeContextPart(part);
      if (context) {
        return context;
      }
    }
  }

  return null;
}
